import type {
  PlaybackData,
  PlaybackNote,
  PlaybackTempo,
} from "@midi-fantom-utils/midi-util";
import { tickToSeconds, secondsToTick } from "./timeline.js";

export type ControllerStatus =
  | "idle"
  | "ready"
  | "playing"
  | "paused"
  | "ended"
  | "error";

export type ControllerState = {
  readonly status: ControllerStatus;
  readonly positionMs: number;
  readonly durationMs: number;
};

export type PlaybackController = {
  getState(): ControllerState;
  subscribe(listener: () => void): () => void;
  play(): void;
  pause(): void;
  stop(): void;
  seek(positionMs: number): void;
  setOnScheduleNote(cb: ((note: PlaybackNote, whenSec: number) => void) | null): void;
  setOnStopAll(cb: (() => void) | null): void;
  dispose(): void;
};

export type PlaybackControllerOptions = {
  /**
   * Транспортный «часы». Требуется либо `audioCtx`, либо `clock`. Если
   * передан `clock` — используется он (например, для MIDI-выхода, где
   * AudioContext не создаётся: `{ now: () => performance.now() / 1000,
   * resume: () => {} }`). Дефолт — производные от `audioCtx`.
   */
  clock?: PlaybackClock;
  audioCtx?: AudioContext | null;
  getNotes: () => ReadonlyArray<PlaybackNote>;
  tempos: PlaybackTempo[];
  ppq: number;
  lookaheadSec?: number;
  onScheduleNote?: (note: PlaybackNote, whenSec: number) => void;
  onStopAll?: () => void;
};

/** Минимальные часы для транспорта: `now()` в секундах + `resume()`. */
export type PlaybackClock = {
  now(): number;
  resume(): void | Promise<void>;
};

const DEFAULT_LOOKAHEAD_SEC = 0.2;

// Планировщик крутится на rAF, но в скрытой вкладке rAF НЕ тикает (браузер
// останавливает кадры) — после опустошения lookahead-окна звук бы оборвался.
// Watchdog ниже ловит stall и переводит цикл на таймерный режим, который
// работает и в фоне. Каденция fallback (100 мс) с lookahead 0.2 с гарантирует,
// что каждое событие планируется минимум за ~100 мс до звучания.
const RAF_STALL_MS = 500;
const FALLBACK_INTERVAL_MS = 100;

const nowMs = (): number => Date.now();

const resolveRaf = (): ((cb: (t: number) => void) => number) | null => {
  if (typeof requestAnimationFrame === "function") {
    return requestAnimationFrame as (cb: (t: number) => void) => number;
  }
  if (typeof window !== "undefined") {
    const w = window as unknown as {
      requestAnimationFrame?: (cb: (t: number) => void) => number;
    };
    if (typeof w.requestAnimationFrame === "function") {
      return w.requestAnimationFrame;
    }
  }
  return null;
};

const resolveCaf = (): ((handle: number) => void) | null => {
  if (typeof cancelAnimationFrame === "function") {
    return cancelAnimationFrame as (handle: number) => void;
  }
  if (typeof window !== "undefined") {
    const w = window as unknown as {
      cancelAnimationFrame?: (handle: number) => void;
    };
    if (typeof w.cancelAnimationFrame === "function") {
      return w.cancelAnimationFrame;
    }
  }
  return null;
};

const findFirstNoteAtOrAfter = (
  notes: ReadonlyArray<PlaybackNote>,
  tick: number
): number => {
  let lo = 0;
  let hi = notes.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid]!.startTicks < tick) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * Длительность плоского `PlaybackData` в миллисекундах.
 *
 * Общий helper — единая точка правды для MidiPlayer (UI до создания
 * AudioContext) и `createPlaybackController` (runtime duration).
 * Если сюда добавятся edge-cases (отрицательные startTicks, length-0
 * tempos и т.п.), оба потребителя увидят фикс одновременно.
 */
export const computePlaybackDurationMs = (data: PlaybackData): number => {
  return computeDurationMs(data.notes, data.tempos, data.ppq);
};

const computeDurationMs = (
  notes: ReadonlyArray<PlaybackNote>,
  tempos: PlaybackTempo[],
  ppq: number
): number => {
  if (notes.length === 0) return 0;
  let max = 0;
  for (const n of notes) {
    const end = n.startTicks + n.durationTicks;
    if (end > max) max = end;
  }
  return Math.round(tickToSeconds(max, tempos, ppq) * 1000);
};

type Transport = {
  startCtxTime: number;
  startTick: number;
};

const shallowEqualState = (a: ControllerState, b: ControllerState): boolean => {
  if (a.status !== b.status) return false;
  if (a.positionMs !== b.positionMs) return false;
  if (a.durationMs !== b.durationMs) return false;
  return true;
};

export const createPlaybackController = (
  opts: PlaybackControllerOptions
): PlaybackController => {
  const { getNotes, tempos, ppq } = opts;
  const lookaheadSec = opts.lookaheadSec ?? DEFAULT_LOOKAHEAD_SEC;

  // Часы транспорта: явный `clock` (MIDI-режим) или производные от audioCtx.
  const clock: PlaybackClock | null =
    opts.clock ??
    (opts.audioCtx
      ? {
          now: () => opts.audioCtx!.currentTime,
          resume: () => opts.audioCtx!.resume(),
        }
      : null);

  const computeDuration = () => computeDurationMs(getNotes(), tempos, ppq);

  let positionMs = 0;
  let status: ControllerStatus = "idle";
  let durationMs = computeDuration();
  let cachedState: ControllerState = Object.freeze({
    status,
    positionMs,
    durationMs,
  });

  const transportRef: { current: Transport | null } = { current: null };
  const pausedAtRef: { current: { tick: number } | null } = { current: null };
  const nextNoteIndexRef: { current: number } = { current: 0 };

  // Гибридный драйвер цикла планировщика: rAF в фореграунде + watchdog
  // setTimeout, который при остановке rAF (фоновая вкладка) переводит цикл
  // на таймерный режим. Без rAF (node, старые браузеры) — сразу таймеры.
  const raf = resolveRaf();
  const caf = resolveCaf();
  let rafId: number | null = null;
  let watchdogId: ReturnType<typeof setTimeout> | null = null;
  let fallbackId: ReturnType<typeof setTimeout> | null = null;
  let lastRafTs = 0;
  let loopCb: (() => void) | null = null;

  const clearFallbackTimer = () => {
    if (fallbackId !== null) {
      clearTimeout(fallbackId);
      fallbackId = null;
    }
  };

  const fallbackStep = () => {
    const cb = loopCb;
    if (!cb) return;
    cb();
    // Пере-армимся только если цикл не был остановлен внутри cb() (end/pause).
    if (loopCb === cb) {
      fallbackId = setTimeout(fallbackStep, FALLBACK_INTERVAL_MS);
    } else {
      fallbackId = null;
    }
  };

  const rafStep = () => {
    lastRafTs = nowMs();
    clearFallbackTimer();
    const cb = loopCb;
    if (!cb) return;
    cb();
    if (loopCb === cb) {
      rafId = raf ? raf(rafStep) : null;
    } else {
      rafId = null;
    }
  };

  const watchdog = () => {
    if (!loopCb) {
      watchdogId = null;
      return;
    }
    // rAF не тикал дольше порога (скрытая вкладка) — включаем таймерный режим.
    if (nowMs() - lastRafTs >= RAF_STALL_MS && fallbackId === null) {
      fallbackId = setTimeout(fallbackStep, 0);
    }
    watchdogId = setTimeout(watchdog, RAF_STALL_MS);
  };

  const stopLoop = () => {
    loopCb = null;
    if (rafId !== null) {
      if (caf) caf(rafId);
      rafId = null;
    }
    if (watchdogId !== null) {
      clearTimeout(watchdogId);
      watchdogId = null;
    }
    clearFallbackTimer();
  };

  const startLoop = (cb: () => void) => {
    stopLoop();
    loopCb = cb;
    lastRafTs = nowMs();
    if (raf) {
      rafId = raf(rafStep);
      watchdogId = setTimeout(watchdog, RAF_STALL_MS);
    } else {
      // Нет rAF — сразу таймерный режим.
      fallbackId = setTimeout(fallbackStep, 0);
    }
  };

  let onScheduleNote: ((note: PlaybackNote, whenSec: number) => void) | null =
    opts.onScheduleNote ?? null;
  let onStopAll: (() => void) | null = opts.onStopAll ?? null;

  const listeners = new Set<() => void>();
  let disposed = false;

  const setState = (
    nextStatus: ControllerStatus,
    nextPositionMs: number,
    nextDurationMs: number
  ) => {
    const next: ControllerState = { status: nextStatus, positionMs: nextPositionMs, durationMs: nextDurationMs };
    if (shallowEqualState(cachedState, next)) return;
    cachedState = Object.freeze(next);
    for (const l of listeners) l();
  };

  const refreshDuration = () => {
    durationMs = computeDuration();
  };

  const tickAt = (ctxTime: number): number => {
    const t = transportRef.current;
    if (!t) return 0;
    const elapsed = ctxTime - t.startCtxTime;
    if (elapsed <= 0) return t.startTick;
    return t.startTick + secondsToTick(elapsed, tempos, ppq);
  };

  const tick = () => {
    if (disposed) return;
    if (!clock) return;
    const t = transportRef.current;
    if (!t) return;
    const currentTick = tickAt(clock.now());
    const lookaheadTicks = secondsToTick(lookaheadSec, tempos, ppq);
    const cutoff = currentTick + lookaheadTicks;
    const arr = getNotes();
    let idx = nextNoteIndexRef.current;
    while (idx < arr.length && arr[idx]!.startTicks <= cutoff) {
      const note = arr[idx]!;
      const tickOffset = note.startTicks - currentTick;
      if (tickOffset < 0) {
        idx++;
        continue;
      }
      const secondsUntilNote = tickToSeconds(tickOffset, tempos, ppq);
      const when = clock.now() + secondsUntilNote;
      try {
        onScheduleNote?.(note, when);
      } catch {
        setState("error", positionMs, durationMs);
        stopLoop();
        transportRef.current = null;
        return;
      }
      idx++;
    }
    nextNoteIndexRef.current = idx;
    const posMs = Math.round(tickToSeconds(currentTick, tempos, ppq) * 1000);
    positionMs = posMs;
    if (idx >= arr.length && arr.length > 0) {
      setState("ended", posMs, durationMs);
      transportRef.current = null;
      pausedAtRef.current = null;
      stopLoop();
      return;
    }
    setState("playing", posMs, durationMs);
  };

  const play = () => {
    if (disposed) return;
    if (!clock) return;
    const arr = getNotes();
    if (arr.length === 0) return;
    if (status === "playing") return;
    void Promise.resolve(clock.resume()).catch(() => undefined);
    refreshDuration();
    const startTick = pausedAtRef.current
      ? pausedAtRef.current.tick
      : secondsToTick(positionMs / 1000, tempos, ppq);
    transportRef.current = {
      startCtxTime: clock.now(),
      startTick,
    };
    pausedAtRef.current = null;
    nextNoteIndexRef.current = findFirstNoteAtOrAfter(arr, startTick);
    status = "playing";
    setState("playing", positionMs, durationMs);
    startLoop(tick);
  };

  const pause = () => {
    if (disposed) return;
    if (!clock) return;
    if (status !== "playing") return;
    const t = transportRef.current;
    if (!t) return;
    const currentTick = tickAt(clock.now());
    pausedAtRef.current = { tick: currentTick };
    transportRef.current = null;
    const posMs = Math.round(tickToSeconds(currentTick, tempos, ppq) * 1000);
    positionMs = posMs;
    status = "paused";
    setState("paused", posMs, durationMs);
    stopLoop();
    onStopAll?.();
  };

  const stop = () => {
    if (disposed) return;
    transportRef.current = null;
    pausedAtRef.current = null;
    nextNoteIndexRef.current = 0;
    positionMs = 0;
    status = "idle";
    refreshDuration();
    setState("idle", 0, durationMs);
    stopLoop();
    onStopAll?.();
  };

  const seek = (targetMs: number) => {
    if (disposed) return;
    if (!clock) return;
    refreshDuration();
    const clamped = Math.max(0, Math.min(targetMs, durationMs));
    const newTick = secondsToTick(clamped / 1000, tempos, ppq);
    const wasPlaying = status === "playing";
    onStopAll?.();
    positionMs = clamped;
    nextNoteIndexRef.current = findFirstNoteAtOrAfter(getNotes(), newTick);
    if (wasPlaying) {
      transportRef.current = {
        startCtxTime: clock.now(),
        startTick: newTick,
      };
      pausedAtRef.current = null;
      status = "playing";
      setState("playing", clamped, durationMs);
      startLoop(tick);
    } else {
      transportRef.current = null;
      pausedAtRef.current = { tick: newTick };
      status = "paused";
      setState("paused", clamped, durationMs);
    }
  };

  return {
    getState: () => cachedState,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    play,
    pause,
    stop,
    seek,
    setOnScheduleNote: (cb) => {
      onScheduleNote = cb;
    },
    setOnStopAll: (cb) => {
      onStopAll = cb;
    },
    dispose: () => {
      disposed = true;
      stopLoop();
      listeners.clear();
    },
  };
};